
import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Package, Plus, Crown, FileText, Warehouse, Tag, FileDown } from "lucide-react";
import { toast } from "@/components/ui/use-toast";
import { useNavigate } from "react-router-dom";
import MainLayout from "@/components/layout/MainLayout";
import { useBateaux } from "@/hooks/useBateaux";
import { useInventaire } from "@/hooks/useInventaire";
import { useProAccess } from "@/hooks/useProAccess";
import SelecteurNavire from "@/components/bateaux/SelecteurNavire";
import FormulaireAjoutArticle from "@/components/inventaire/FormulaireAjoutArticle";
import FormulaireModificationArticle from "@/components/inventaire/FormulaireModificationArticle";
import VueCompacteCategories from "@/components/inventaire/VueCompacteCategories";
import ActionsEnLotInventaire from "@/components/inventaire/ActionsEnLotInventaire";
import GenerateurAvitaillement from "@/components/inventaire/GenerateurAvitaillement";
import GestionListesAvitaillement from "@/components/inventaire/GestionListesAvitaillement";
import GestionEspacesStockage from "@/components/inventaire/GestionEspacesStockage";
import GestionCategories from "@/components/inventaire/GestionCategories";
import ListeAchat from '@/components/inventaire/ListeAchat';
import ListeAchatAvitaillement from '@/components/inventaire/ListeAchatAvitaillement';
import ListeEquipementSecurite from '@/components/inventaire/ListeEquipementSecurite';
import Division240Selector from '@/components/inventaire/Division240Selector';
import RechercheArticles from '@/components/inventaire/RechercheArticles';
import { useEspacesStockage } from '@/hooks/useEspacesStockage';
import { ListeAvitaillement } from '@/hooks/useListesAvitaillement';
import PremiumLimitBanner from '@/components/premium/PremiumLimitBanner';
import HiddenDataBanner from '@/components/premium/HiddenDataBanner';
import InventairePDFDialog from '@/components/inventaire/InventairePDFDialog';
import { useTranslation } from 'react-i18next';

const FREE_ARTICLES_LIMIT = 5;

const InventoryPage = () => {
  const { t } = useTranslation('inventory');
  const navigate = useNavigate();
  const { bateaux, loading: loadingBateaux, getBateauParDefaut } = useBateaux();
  const { articles, loading: loadingInventaire, supprimerArticle, refetchArticles } = useInventaire();
  const { isProAccount } = useProAccess();
  const { espaces } = useEspacesStockage();
  const [selectedBateauId, setSelectedBateauId] = useState<string | null>(null);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isEspacesDialogOpen, setIsEspacesDialogOpen] = useState(false);
  const [isCategoriesDialogOpen, setIsCategoriesDialogOpen] = useState(false);
  const [isListeAchatDialogOpen, setIsListeAchatDialogOpen] = useState(false);
  const [isEquipementSecuriteDialogOpen, setIsEquipementSecuriteDialogOpen] = useState(false);
  const [isPDFDialogOpen, setIsPDFDialogOpen] = useState(false);
  const [isListeAchatAvitaillementDialogOpen, setIsListeAchatAvitaillementDialogOpen] = useState(false);
  const [selectedListeAvitaillement, setSelectedListeAvitaillement] = useState<ListeAvitaillement | null>(null);
  const [editingArticle, setEditingArticle] = useState<any>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [activeTab, setActiveTab] = useState("inventaire");

  // Auto-sélectionner le navire par défaut
  React.useEffect(() => {
    if (!loadingBateaux && bateaux.length > 0 && !selectedBateauId) {
      const bateauParDefaut = getBateauParDefaut();
      if (bateauParDefaut) {
        setSelectedBateauId(bateauParDefaut.id);
      }
    }
  }, [bateaux, loadingBateaux, selectedBateauId, getBateauParDefaut]);

  const handleRefresh = async () => {
    console.log('Force refreshing from InventoryPage...');
    if (selectedBateauId) {
      await refetchArticles(selectedBateauId);
    } else {
      await refetchArticles();
    }
    setRefreshKey(prev => prev + 1);
  };

  const handleCreerInventaireFromAvitaillement = () => {
    setActiveTab("inventaire");
    toast({
      title: t('page.inventoryOpened'),
      description: t('page.inventoryOpenedDescription'),
    });
  };

  if (loadingBateaux || loadingInventaire) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-[80vh]">
          <div className="text-center p-4 lg:p-6 bg-white rounded-lg shadow-sm">
            <Package className="mx-auto h-8 lg:h-12 w-8 lg:w-12 text-nautic animate-pulse" />
            <h3 className="mt-2 text-lg lg:text-xl font-semibold text-nautic">{t('page.loading')}</h3>
            <p className="mt-1 text-sm lg:text-base text-gray-500">
              {t('page.loadingDescription')}
            </p>
          </div>
        </div>
      </MainLayout>
    );
  }

  const articlesFiltresComplets = selectedBateauId 
    ? articles.filter(a => a.bateau_id === selectedBateauId)
    : articles;

  const hiddenArticlesCount = !isProAccount && articlesFiltresComplets.length > FREE_ARTICLES_LIMIT 
    ? articlesFiltresComplets.length - FREE_ARTICLES_LIMIT 
    : 0;

  const articlesFiltres = !isProAccount 
    ? articlesFiltresComplets.slice(0, FREE_ARTICLES_LIMIT) 
    : articlesFiltresComplets;

  console.log('Articles filtrés pour actions:', articlesFiltres.length, selectedBateauId);

  const articlesVides = articlesFiltres.filter(a => a.quantite === 0);
  const articlesCritiques = articlesFiltres.filter(a => {
    const pourcentage = (a.quantite / a.quantite_max) * 100;
    return pourcentage <= 20 && pourcentage > 0;
  });

  const bateauSelectionne = bateaux.find(b => b.id === selectedBateauId);
  const nomBateau = bateauSelectionne ? `${bateauSelectionne.nom} - ${bateauSelectionne.nom_constructeur} ${bateauSelectionne.nom_modele}` : "";

  const handleEdit = (article: any) => {
    setEditingArticle(article);
    setIsEditDialogOpen(true);
  };

  const handleArticleSearchSelect = (article: any) => {
    handleEdit(article);
  };

  const handleEditComplete = async () => {
    setIsEditDialogOpen(false);
    setEditingArticle(null);
    await handleRefresh();
  };

  const handleDelete = async (articleId: string) => {
    await supprimerArticle(articleId);
    await handleRefresh();
  };

  const handleArticleAdded = async () => {
    setIsAddDialogOpen(false);
    await handleRefresh();
  };

  const handleCreerListeAchat = () => {
    if (!selectedBateauId) {
      toast({
        title: t('page.error'),
        description: t('page.selectBoat'),
        variant: "destructive",
      });
      return;
    }
    setIsListeAchatDialogOpen(true);
  };

  const handleCreerListeEquipement = () => {
    if (!selectedBateauId) {
      toast({
        title: t('page.error'),
        description: t('page.selectBoat'),
        variant: "destructive",
      });
      return;
    }
    
    setIsEquipementSecuriteDialogOpen(true);
  };

  const handleCreerListeAchatFromAvitaillement = (liste: ListeAvitaillement) => {
    setSelectedListeAvitaillement(liste);
    setIsListeAchatAvitaillementDialogOpen(true);
  };

  const handleGenererPDF = (liste: any) => {
    // Logic to generate PDF
    console.log('Generating PDF for list:', liste);
  };

  return (
    <MainLayout>
      <div className="space-y-4 lg:space-y-6 p-4 lg:p-0" key={refreshKey}>
        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
          <div>
            <h1 className="text-xl lg:text-3xl font-bold text-nautic flex items-center gap-2 flex-wrap">
              {t('page.title')}
              {!isProAccount && (
                <>
                  <Crown className="h-4 lg:h-6 w-4 lg:w-6 text-nautic-gold" />
                  <span className="text-sm lg:text-lg text-gray-600">{t('page.freeAccountArticles', { count: FREE_ARTICLES_LIMIT })}</span>
                </>
              )}
            </h1>
            <p className="text-sm lg:text-base text-muted-foreground mt-1">
              {t('page.subtitle')}
            </p>
          </div>

          {hiddenArticlesCount > 0 && (
            <HiddenDataBanner hiddenCount={hiddenArticlesCount} itemLabel="article" />
          )}
          
          <div className="flex gap-2 flex-wrap">
            <Dialog open={isPDFDialogOpen} onOpenChange={setIsPDFDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="w-full lg:w-auto border-nautic-gold text-nautic hover:bg-nautic-gold/20" disabled={!selectedBateauId}>
                  <FileDown className="h-4 w-4 mr-2" />
                  {t('page.inventoryPdfButton')}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto mx-4">
                <DialogHeader>
                  <DialogTitle>{t('page.exportPdfTitle')}</DialogTitle>
                </DialogHeader>
                <InventairePDFDialog
                  articles={articlesFiltres}
                  bateau={bateauSelectionne}
                  espacesStockage={espaces}
                  nomBateau={nomBateau}
                />
              </DialogContent>
            </Dialog>

            <Dialog open={isEspacesDialogOpen} onOpenChange={setIsEspacesDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="w-full lg:w-auto">
                  <Warehouse className="h-4 w-4 mr-2" />
                  {t('page.storageSpacesButton')}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto mx-4">
                <DialogHeader>
                  <DialogTitle>{t('page.storageSpacesTitle')}</DialogTitle>
                </DialogHeader>
                
                <GestionEspacesStockage selectedBateauId={selectedBateauId || undefined} />
              </DialogContent>
            </Dialog>

            <Dialog open={isCategoriesDialogOpen} onOpenChange={setIsCategoriesDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="w-full lg:w-auto">
                  <Tag className="h-4 w-4 mr-2" />
                  {t('page.categoriesButton')}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto mx-4">
                <DialogHeader>
                  <DialogTitle>{t('page.categoriesTitle')}</DialogTitle>
                </DialogHeader>
                
                <GestionCategories />
              </DialogContent>
            </Dialog>
            <Button 
              onClick={handleCreerListeEquipement}
              variant="outline" 
              className="w-full lg:w-auto border-nautic text-nautic hover:bg-nautic-light hover:text-white"
              disabled={!selectedBateauId}
            >
              <Package className="h-4 w-4 mr-2" />
              {t('page.equipmentSecurityButton')}
            </Button>

            <Dialog open={isAddDialogOpen} onOpenChange={(open) => {
              if (open && !isProAccount && articlesFiltres.length >= FREE_ARTICLES_LIMIT) {
                toast({
                  title: t('page.limitReached'),
                  description: t('page.limitReachedDescription', { limit: FREE_ARTICLES_LIMIT }),
                  variant: "destructive",
                });
                return;
              }
              setIsAddDialogOpen(open);
            }}>
              <DialogTrigger asChild>
                <Button className="bg-nautic-gold text-nautic hover:bg-nautic-lightgold w-full lg:w-auto">
                  <Plus className="h-4 w-4 mr-2" />
                  {t('page.addArticleButton')}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg lg:max-w-2xl max-h-[90vh] overflow-y-auto mx-4">
                <DialogHeader>
                  <DialogTitle>{t('page.addArticleTitle')}</DialogTitle>
                </DialogHeader>
                
                <FormulaireAjoutArticle 
                  selectedBateauId={selectedBateauId || undefined}
                  onArticleAdded={handleArticleAdded}
                />
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <SelecteurNavire 
          bateaux={bateaux}
          selectedBateauId={selectedBateauId}
          onBateauChange={setSelectedBateauId}
        />

        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="max-w-lg lg:max-w-2xl max-h-[90vh] overflow-y-auto mx-4">
            <DialogHeader>
              <DialogTitle>{t('page.editArticleTitle')}</DialogTitle>
            </DialogHeader>
            
            {editingArticle && (
              <FormulaireModificationArticle
                article={editingArticle}
                onClose={handleEditComplete}
                onSuccess={handleEditComplete}
              />
            )}
          </DialogContent>
        </Dialog>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-6">
          <Card>
            <CardContent className="p-3 lg:p-6">
              <div className="flex items-center gap-2 lg:gap-3">
                <div className="p-1.5 lg:p-2 bg-nautic-light rounded-lg">
                  <Package className="h-4 lg:h-5 w-4 lg:w-5 text-white" />
                </div>
                <div>
                  <p className="text-xs lg:text-sm text-muted-foreground">{t('page.totalArticles')}</p>
                  <p className="text-lg lg:text-2xl font-bold text-nautic">{articlesFiltres.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-3 lg:p-6">
              <div className="flex items-center gap-2 lg:gap-3">
                <div className="p-1.5 lg:p-2 bg-orange-100 rounded-lg">
                  <Package className="h-4 lg:h-5 w-4 lg:w-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-xs lg:text-sm text-muted-foreground">{t('page.criticalStock')}</p>
                  <p className="text-lg lg:text-2xl font-bold text-orange-600">{articlesCritiques.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-3 lg:p-6">
              <div className="flex items-center gap-2 lg:gap-3">
                <div className="p-1.5 lg:p-2 bg-red-100 rounded-lg">
                  <Package className="h-4 lg:h-5 w-4 lg:w-5 text-red-600" />
                </div>
                <div>
                  <p className="text-xs lg:text-sm text-muted-foreground">{t('page.emptyStock')}</p>
                  <p className="text-lg lg:text-2xl font-bold text-red-600">{articlesVides.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 h-14 lg:h-16 bg-nautic/10">
            <TabsTrigger 
              value="inventaire" 
              className="text-sm lg:text-lg font-semibold py-3 lg:py-4 data-[state=active]:bg-nautic data-[state=active]:text-nautic-gold data-[state=inactive]:text-nautic hover:bg-nautic/20 transition-all"
            >
              <Package className="h-5 lg:h-6 w-5 lg:w-6 mr-2 lg:mr-3" />
              {t('page.inventoryTab')}
            </TabsTrigger>
            <TabsTrigger 
              value="avitaillement" 
              className="text-sm lg:text-lg font-semibold py-3 lg:py-4 data-[state=active]:bg-nautic data-[state=active]:text-nautic-gold data-[state=inactive]:text-nautic hover:bg-nautic/20 transition-all"
            >
              <FileText className="h-5 lg:h-6 w-5 lg:w-6 mr-2 lg:mr-3" />
              <span className="hidden lg:inline">{t('page.supplyTab')}</span>
              <span className="lg:hidden">{t('page.supplyTabShort')}</span>
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="inventaire" className="space-y-4 lg:space-y-6">
            {!isProAccount && articlesFiltres.length >= FREE_ARTICLES_LIMIT && (
              <PremiumLimitBanner 
                title={t('page.articlesLimitReachedTitle')}
                description={t('page.articlesLimitReachedDescription', { limit: FREE_ARTICLES_LIMIT })}
              />
            )}

            {selectedBateauId && (
              <Division240Selector
                selectedBateauId={selectedBateauId}
                onEquipmentAdded={handleRefresh}
              />
            )}
            
            <ActionsEnLotInventaire 
              articles={articlesFiltres} 
              bateauId={selectedBateauId || undefined}
              nomBateau={nomBateau}
              onRefresh={handleRefresh}
            />
            
            {/* Barre de recherche */}
            <div className="space-y-2">
              <h3 className="text-lg font-semibold text-nautic">{t('page.searchArticleTitle')}</h3>
              <RechercheArticles 
                bateauId={selectedBateauId || undefined}
                onArticleSelect={handleArticleSearchSelect}
              />
            </div>
            
            <VueCompacteCategories 
              onEdit={handleEdit}
              onDelete={handleDelete}
              showActions={true}
              bateauId={selectedBateauId || undefined}
              onRefresh={handleRefresh}
              articlesOverride={articlesFiltres}
            />
          </TabsContent>
          
          <TabsContent value="avitaillement" className="space-y-4 lg:space-y-6">
            <GenerateurAvitaillement 
              bateaux={bateaux}
              selectedBateauId={selectedBateauId || undefined}
              onRefresh={handleRefresh}
            />
            <GestionListesAvitaillement
              bateaux={bateaux}
              selectedBateauId={selectedBateauId || undefined}
              onCreerListeAchat={handleCreerListeAchatFromAvitaillement}
              onGenererPDF={handleGenererPDF}
            />
          </TabsContent>
        </Tabs>

        <Dialog open={isListeAchatDialogOpen} onOpenChange={setIsListeAchatDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto mx-4">
            <DialogHeader>
              <DialogTitle>{t('page.purchaseListTitle')}</DialogTitle>
            </DialogHeader>
            
            <ListeAchat 
              articles={articlesFiltres}
              nomBateau={nomBateau}
            />
          </DialogContent>
        </Dialog>

        <Dialog open={isEquipementSecuriteDialogOpen} onOpenChange={setIsEquipementSecuriteDialogOpen}>
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto mx-4">
            <DialogHeader>
              <DialogTitle>{t('page.securityEquipmentListTitle')}</DialogTitle>
            </DialogHeader>
            
            <ListeEquipementSecurite 
              articles={articlesFiltres}
              bateau={bateauSelectionne}
              espacesStockage={espaces}
              onClose={() => setIsEquipementSecuriteDialogOpen(false)}
            />
          </DialogContent>
        </Dialog>

        <Dialog open={isListeAchatAvitaillementDialogOpen} onOpenChange={setIsListeAchatAvitaillementDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto mx-4">
            <DialogHeader>
              <DialogTitle>{t('page.supplyPurchaseListTitle')}</DialogTitle>
            </DialogHeader>
            
            {selectedListeAvitaillement && (
              <ListeAchatAvitaillement 
                liste={selectedListeAvitaillement}
                nomBateau={nomBateau}
              />
            )}
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
};

export default InventoryPage;
